import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from 'sonner';
import { axiosInstance } from '@/App';
import ShiftForm from '@/components/roster/ShiftForm';
import { startOfWeek, endOfWeek, format, addDays, differenceInDays, subDays } from 'date-fns';
import { Calendar, ClipboardCheck, Cloud, CloudOff, Loader2, UserX, Users } from 'lucide-react';

// Maximum days roster can be backdated (from 27.3-CONTEXT.md)
const MAX_BACKDATE_DAYS = 7;

export default function RosterManagement() {
  const [selectedWeek, setSelectedWeek] = useState(new Date());
  const [shifts, setShifts] = useState([]);
  const [showShiftForm, setShowShiftForm] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [editingShift, setEditingShift] = useState(null); // Shift being edited
  const [pendingRosters, setPendingRosters] = useState([]);
  const [loadingPending, setLoadingPending] = useState(false);
  // Get user role from localStorage (must be before useState that depends on isAdmin)
  const userStr = localStorage.getItem('user');
  const user = userStr ? JSON.parse(userStr) : null;
  const isAdmin = user?.role === 'admin';
  const isManager = user?.role === 'manager';
  const canViewApprovals = isAdmin || isManager; // EDGE-09: Manager can view all, approve own gyms

  const [activeTab, setActiveTab] = useState(isAdmin ? 'pending' : 'create');
  const [approvedRosters, setApprovedRosters] = useState([]);
  const [loadingApproved, setLoadingApproved] = useState(false);
  const [editingPendingRoster, setEditingPendingRoster] = useState(null); // Pending roster being edited

  // Auto-save draft state
  const [draftRosterId, setDraftRosterId] = useState(null);
  const [rosterStatus, setRosterStatus] = useState(null); // 'draft', 'submitted', 'approved', or null
  const [saveStatus, setSaveStatus] = useState('idle'); // 'idle', 'saving', 'saved', 'error'
  const [loadingDraft, setLoadingDraft] = useState(false);
  const isInitialMount = useRef(true);

  // Team members for "not in roster" tracking
  const [teamMembers, setTeamMembers] = useState([]);

  // Memoize weekStart/weekEnd to prevent infinite re-renders in useEffect
  const weekStart = useMemo(() => startOfWeek(selectedWeek, { weekStartsOn: 1 }), [selectedWeek]);
  const weekEnd = useMemo(() => endOfWeek(selectedWeek, { weekStartsOn: 1 }), [selectedWeek]);

  // Fetch team members on mount (for managers)
  useEffect(() => {
    if (isManager) {
      const fetchTeamMembers = async () => {
        try {
          const response = await axiosInstance.get('/team-members');
          setTeamMembers(response.data || []);
        } catch (error) {
          console.error('Failed to fetch team members:', error);
        }
      };
      fetchTeamMembers();
    }
  }, [isManager]);

  // Calculate team members not in roster for current week
  const membersNotInRoster = useMemo(() => {
    if (!isManager || teamMembers.length === 0) return [];

    // Get all user IDs that have at least one shift in current draft
    const usersWithShifts = new Set(shifts.map(s => s.user_id));

    // Filter team members who don't have shifts
    return teamMembers.filter(member => !usersWithShifts.has(member.id));
  }, [isManager, teamMembers, shifts]);

  // State for confirmation dialog when editing approved roster
  const [showEditConfirmDialog, setShowEditConfirmDialog] = useState(false);
  const [editingRosterId, setEditingRosterId] = useState(null);
  const [editingRosterStatus, setEditingRosterStatus] = useState(null);

  // Calculate backdate warning
  const backdateInfo = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const weekStartDate = new Date(weekStart);
    weekStartDate.setHours(0, 0, 0, 0);

    const daysBackdated = differenceInDays(today, weekStartDate);

    return {
      daysBackdated,
      isBackdated: daysBackdated > 0,
      exceedsLimit: daysBackdated > MAX_BACKDATE_DAYS,
      message: daysBackdated > MAX_BACKDATE_DAYS
        ? `Rosters cannot be backdated more than ${MAX_BACKDATE_DAYS} days. This week is ${daysBackdated} days in the past.`
        : daysBackdated > 0
        ? `This roster is backdated ${daysBackdated} day(s).`
        : null
    };
  }, [weekStart]);

  // ============= AUTO-SAVE DRAFT FUNCTIONS =============

  // Load existing draft roster when week changes
  const loadDraft = useCallback(async (weekStartStr) => {
    if (!isManager) return;

    setLoadingDraft(true);
    try {
      const response = await axiosInstance.get('/rosters/draft', {
        params: { week_start_date: weekStartStr }
      });

      if (response.data) {
        // Roster exists - load it
        setShifts(response.data.shifts || []);
        setDraftRosterId(response.data.roster_id);
        setRosterStatus(response.data.status || 'draft');
        console.log('Roster loaded:', response.data.roster_id, 'status:', response.data.status, 'with', response.data.shifts?.length, 'shifts');
      } else {
        // No roster - start fresh
        setShifts([]);
        setDraftRosterId(null);
        setRosterStatus(null);
      }
    } catch (error) {
      // 404 means no roster exists - that's fine
      if (error.response?.status !== 404) {
        console.error('Failed to load roster:', error);
      }
      setShifts([]);
      setDraftRosterId(null);
      setRosterStatus(null);
    } finally {
      setLoadingDraft(false);
    }
  }, [isManager]);

  // Save draft to database
  const saveDraft = useCallback(async (currentShifts) => {
    if (!isManager || currentShifts.length === 0) return;

    setSaveStatus('saving');
    try {
      const roster = {
        week_start_date: format(weekStart, 'yyyy-MM-dd'),
        week_end_date: format(weekEnd, 'yyyy-MM-dd'),
        shifts: currentShifts
      };

      const response = await axiosInstance.put('/rosters/draft', roster);
      setDraftRosterId(response.data.roster_id);
      setSaveStatus('saved');

      // Clear 'saved' status after 2 seconds
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (error) {
      console.error('Failed to save draft:', error);
      setSaveStatus('error');
      toast.error('Failed to auto-save draft');
    }
  }, [isManager, weekStart, weekEnd]);

  // Load draft when week changes (but skip if editing a pending roster)
  useEffect(() => {
    if (isManager && !editingPendingRoster) {
      const weekStartStr = format(weekStart, 'yyyy-MM-dd');
      loadDraft(weekStartStr);
    }
  }, [weekStart, isManager, loadDraft, editingPendingRoster]);

  // Auto-save when shifts change (debounced)
  useEffect(() => {
    // Skip initial mount to avoid saving empty state
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    // Skip if loading draft (to avoid overwriting with stale data)
    if (loadingDraft) return;

    // Skip if no shifts (don't save empty draft)
    if (shifts.length === 0) return;

    // Skip auto-save for approved/submitted rosters (view-only mode)
    if (rosterStatus === 'approved' || rosterStatus === 'submitted') return;

    // Debounce save to avoid too many API calls
    const timeoutId = setTimeout(() => {
      saveDraft(shifts);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [shifts, saveDraft, loadingDraft, rosterStatus]);

  // Fetch pending rosters for Admin and Managers (EDGE-09)
  useEffect(() => {
    if (canViewApprovals) {
      fetchPendingRosters();
    }
  }, [canViewApprovals]);

  // Fetch approved rosters for admins when week changes
  useEffect(() => {
    if (isAdmin) {
      fetchApprovedRosters();
    }
  }, [weekStart, isAdmin]);

  const fetchPendingRosters = async () => {
    setLoadingPending(true);
    try {
      const response = await axiosInstance.get('/rosters/pending');
      setPendingRosters(response.data || []);
    } catch (error) {
      console.error('Failed to fetch pending rosters:', error);
      toast.error('Failed to load pending rosters');
    } finally {
      setLoadingPending(false);
    }
  };

  const fetchApprovedRosters = async () => {
    setLoadingApproved(true);
    try {
      const weekStartStr = format(weekStart, 'yyyy-MM-dd');
      const response = await axiosInstance.get('/rosters/all-approved', {
        params: { week_start_date: weekStartStr }
      });
      setApprovedRosters(response.data?.rosters || []);
    } catch (error) {
      console.error('Failed to fetch approved rosters:', error);
      toast.error('Failed to load approved rosters');
    } finally {
      setLoadingApproved(false);
    }
  };

  const handleApproveRoster = async (rosterId) => {
    try {
      await axiosInstance.put(`/rosters/${rosterId}/approve`);
      toast.success('Roster approved successfully');
      fetchPendingRosters(); // Refresh the list
    } catch (error) {
      const detail = error.response?.data?.detail;
      const errorMsg = typeof detail === 'string' ? detail : 'Failed to approve roster';
      toast.error(errorMsg);
    }
  };

  const handleRejectRoster = async (rosterId) => {
    const reason = prompt('Enter rejection reason:');
    if (!reason) return;

    try {
      await axiosInstance.put(`/rosters/${rosterId}/reject`, { reason });
      toast.success('Roster rejected');
      fetchPendingRosters(); // Refresh the list
    } catch (error) {
      const detail = error.response?.data?.detail;
      const errorMsg = typeof detail === 'string' ? detail : 'Failed to reject roster';
      toast.error(errorMsg);
    }
  };

  const handleCopyPreviousWeek = async () => {
    try {
      const response = await axiosInstance.post('/rosters/copy-previous-week', {
        current_week_start: format(weekStart, 'yyyy-MM-dd')
      });

      setShifts(response.data.shifts || []);
      toast.success(response.data.message || 'Previous week copied successfully');
    } catch (error) {
      const detail = error.response?.data?.detail;
      const errorMsg = typeof detail === 'string' ? detail :
        (Array.isArray(detail) ? detail[0]?.msg : 'Failed to copy previous week');
      toast.error(errorMsg || 'Failed to copy previous week');
      console.error(error);
    }
  };

  const handleAddShift = (date) => {
    setSelectedDate(format(date, 'yyyy-MM-dd'));
    setEditingShift(null); // Clear any editing state
    setShowShiftForm(true);
  };

  // Edit an existing shift
  const handleEditShift = (shift) => {
    setSelectedDate(shift.date);
    setEditingShift(shift);
    setShowShiftForm(true);
  };

  const handleSaveShift = (shift) => {
    if (editingShift) {
      // Update existing shift
      setShifts(shifts.map(s => s.shift_id === shift.shift_id ? shift : s));
      toast.success('Shift updated');
    } else {
      // Add new shift
      setShifts([...shifts, shift]);
      toast.success('Shift added');
    }
    setShowShiftForm(false);
    setEditingShift(null);
  };

  // Copy previous day's shifts to selected day
  const handleCopyPreviousDay = (targetDate) => {
    const targetDateStr = format(targetDate, 'yyyy-MM-dd');
    const previousDate = subDays(targetDate, 1);
    const previousDateStr = format(previousDate, 'yyyy-MM-dd');

    const previousDayShifts = shifts.filter(s => s.date === previousDateStr);

    if (previousDayShifts.length === 0) {
      toast.error('No shifts to copy from previous day');
      return;
    }

    // Copy shifts with new date and new shift_id
    const copiedShifts = previousDayShifts.map(shift => ({
      ...shift,
      shift_id: Math.random().toString(36).substr(2, 9),
      date: targetDateStr
    }));

    // Remove existing shifts for target date and add copied ones
    const otherShifts = shifts.filter(s => s.date !== targetDateStr);
    setShifts([...otherShifts, ...copiedShifts]);
    toast.success(`Copied ${copiedShifts.length} shift(s) from ${format(previousDate, 'MMM d')}`);
  };

  // Delete a shift
  const handleDeleteShift = (shiftId) => {
    setShifts(shifts.filter(s => s.shift_id !== shiftId));
    toast.success('Shift removed');
  };

  // Load a pending roster for editing (manager can edit their own pending rosters)
  const handleEditPendingRoster = (roster) => {
    // IMPORTANT: Set editing state FIRST to prevent loadDraft from overwriting shifts
    setEditingPendingRoster(roster);

    // Parse the week start date to set the selected week
    const [year, month, day] = roster.week_start_date.split('-').map(Number);
    const weekDate = new Date(year, month - 1, day);
    setSelectedWeek(weekDate);

    // Load the shifts from the pending roster
    setShifts(roster.shifts || []);

    // Disable initial mount flag so we don't overwrite with auto-save
    isInitialMount.current = false;

    // Switch to create tab
    setActiveTab('create');

    toast.info('Roster loaded for editing. Make your changes and save/submit.');
  };

  const handleSubmitRoster = async () => {
    if (shifts.length === 0) {
      toast.error('Please add at least one shift before submitting');
      return;
    }

    try {
      // If we have a draft, submit it
      if (draftRosterId) {
        const response = await axiosInstance.put(`/rosters/${draftRosterId}/submit`);

        if (response.data.conflicts && response.data.conflicts.length > 0) {
          toast.warning(`Roster submitted with ${response.data.conflicts.length} conflicts. Admin will review.`);
        } else {
          toast.success(response.data.message || 'Roster submitted for approval');
        }

        // Clear local state after submission
        setShifts([]);
        setDraftRosterId(null);
      } else {
        // No draft exists - save first then submit
        const roster = {
          week_start_date: format(weekStart, 'yyyy-MM-dd'),
          week_end_date: format(weekEnd, 'yyyy-MM-dd'),
          shifts: shifts
        };

        // Save as draft first
        const draftResponse = await axiosInstance.put('/rosters/draft', roster);
        const newDraftId = draftResponse.data.roster_id;

        // Then submit
        const response = await axiosInstance.put(`/rosters/${newDraftId}/submit`);

        if (response.data.conflicts && response.data.conflicts.length > 0) {
          toast.warning(`Roster submitted with ${response.data.conflicts.length} conflicts. Admin will review.`);
        } else {
          toast.success(response.data.message || 'Roster submitted for approval');
        }

        // Clear shifts after submission
        setShifts([]);
        setDraftRosterId(null);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit roster');
      console.error(error);
    }
  };

  // Update a pending roster (when manager edits their own pending roster)
  const handleUpdatePendingRoster = async () => {
    if (!editingPendingRoster) return;

    if (shifts.length === 0) {
      toast.error('Please add at least one shift');
      return;
    }

    try {
      const response = await axiosInstance.put(`/rosters/${editingPendingRoster.roster_id}/edit`, {
        week_start_date: editingPendingRoster.week_start_date,
        week_end_date: editingPendingRoster.week_end_date,
        shifts: shifts
      });

      if (response.data.conflicts && response.data.conflicts.length > 0) {
        toast.warning(`Roster updated with ${response.data.conflicts.length} conflicts. Admin will review.`);
      } else {
        toast.success(response.data.message || 'Roster updated successfully');
      }

      // Clear editing state
      setEditingPendingRoster(null);
      setShifts([]);
      setDraftRosterId(null);
      setRosterStatus(null);

      // Refresh pending rosters
      fetchPendingRosters();

      // Switch back to approvals tab
      setActiveTab('approvals');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update roster');
      console.error(error);
    }
  };

  // Roster Creation View Component (reusable)
  const RosterCreationView = () => (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Calendar className="h-5 w-5" />
          Create Team Roster
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Approved roster banner */}
          {rosterStatus === 'approved' && !editingPendingRoster && (
            <div className="p-4 bg-green-50 border-2 border-green-500 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <ClipboardCheck className="h-6 w-6 text-green-600" />
                  <div>
                    <h4 className="font-bold text-green-800 text-lg">Roster Approved</h4>
                    <p className="text-green-700 text-sm">
                      This week's roster has been approved by admin. Click "Edit Roster" to make changes (requires re-approval).
                    </p>
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setEditingPendingRoster({
                      roster_id: draftRosterId,
                      week_start_date: format(weekStart, 'yyyy-MM-dd'),
                      week_end_date: format(weekEnd, 'yyyy-MM-dd'),
                      shifts: shifts,
                      team_name: 'Current Roster'
                    });
                  }}
                  className="border-amber-500 text-amber-700 hover:bg-amber-50 whitespace-nowrap"
                >
                  ✎ Edit Roster
                </Button>
              </div>
            </div>
          )}

          {/* Submitted roster banner */}
          {rosterStatus === 'submitted' && (
            <div className="p-4 bg-blue-50 border-2 border-blue-500 rounded-lg">
              <div className="flex items-center gap-3">
                <Loader2 className="h-6 w-6 text-blue-600" />
                <div>
                  <h4 className="font-bold text-blue-800 text-lg">Roster Pending Approval</h4>
                  <p className="text-blue-700 text-sm">
                    This roster has been submitted and is waiting for admin approval.
                    You can view the shifts below but cannot make changes until it's reviewed.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Backdate warning banner */}
          {backdateInfo.exceedsLimit && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700 font-medium">
                {backdateInfo.message}
              </p>
            </div>
          )}
          {backdateInfo.isBackdated && !backdateInfo.exceedsLimit && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-700">
                {backdateInfo.message}
              </p>
            </div>
          )}

          {/* Not in Roster Warning - only show for drafts */}
          {membersNotInRoster.length > 0 && rosterStatus !== 'approved' && rosterStatus !== 'submitted' && (
            <div className="p-4 bg-orange-50 border-2 border-orange-400 rounded-lg">
              <div className="flex items-start gap-3">
                <UserX className="h-6 w-6 text-orange-600 mt-1 flex-shrink-0" />
                <div className="flex-1">
                  <h4 className="font-bold text-orange-800 text-lg flex items-center gap-2">
                    <span>⚠️</span>
                    {membersNotInRoster.length} Team Member{membersNotInRoster.length > 1 ? 's' : ''} Not Yet Scheduled
                  </h4>
                  <p className="text-orange-700 text-sm mt-1">
                    The following team members don't have shifts assigned for this week yet:
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {membersNotInRoster.map((member, idx) => (
                      <span
                        key={idx}
                        className="inline-flex items-center gap-1 px-3 py-1 bg-white border border-orange-300 rounded-full text-sm"
                      >
                        <span className="font-medium text-orange-800">{member.name}</span>
                        <span className="text-orange-600 text-xs">
                          ({member.role === 'team_leader' ? 'TL' :
                            member.role === 'fitness_manager' ? 'FM' :
                            member.role === 'manager' ? 'Mgr' : member.role})
                        </span>
                      </span>
                    ))}
                  </div>
                  <p className="text-orange-600 text-xs mt-3 italic">
                    Add shifts for all team members before submitting the roster.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* All Scheduled Success */}
          {membersNotInRoster.length === 0 && teamMembers.length > 0 && shifts.length > 0 && (
            <div className="p-3 bg-green-50 border border-green-300 rounded-lg">
              <div className="flex items-center gap-2">
                <Users className="h-5 w-5 text-green-600" />
                <span className="text-green-700 font-medium">
                  All {teamMembers.length} team members have shifts scheduled for this week
                </span>
              </div>
            </div>
          )}

          {/* Editing roster indicator */}
          {editingPendingRoster && (
            <div className={`p-4 border-2 rounded-lg ${rosterStatus === 'approved' ? 'bg-amber-50 border-amber-400' : 'bg-blue-50 border-blue-400'}`}>
              <div className="flex justify-between items-center">
                <div>
                  <h4 className={`font-bold ${rosterStatus === 'approved' ? 'text-amber-800' : 'text-blue-800'}`}>
                    {rosterStatus === 'approved' ? 'Editing Approved Roster' : 'Editing Pending Roster'}
                  </h4>
                  <p className={`text-sm ${rosterStatus === 'approved' ? 'text-amber-700' : 'text-blue-700'}`}>
                    {rosterStatus === 'approved'
                      ? 'Make your changes and click "Update Roster" to save. The roster will be reset to pending and require re-approval.'
                      : 'Make your changes and click "Update Roster" to save. The roster will remain pending until approved by admin.'}
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setEditingPendingRoster(null);
                    setShifts([]);
                    loadDraft(format(weekStart, 'yyyy-MM-dd'));
                  }}
                  className="text-blue-600 border-blue-400"
                >
                  Cancel Edit
                </Button>
              </div>
            </div>
          )}

          <div className="flex gap-2 flex-wrap items-center">
            <Button onClick={handleCopyPreviousWeek}>Copy Previous Week</Button>
            <Button variant="outline" onClick={() => setShifts([])}>Clear All</Button>
            {editingPendingRoster ? (
              <Button
                onClick={handleUpdatePendingRoster}
                disabled={shifts.length === 0 || backdateInfo.exceedsLimit}
                title={backdateInfo.exceedsLimit ? backdateInfo.message : ''}
                className="bg-blue-600 hover:bg-blue-700"
              >
                Update Roster ({shifts.length} shifts)
              </Button>
            ) : (
              <Button
                onClick={handleSubmitRoster}
                disabled={shifts.length === 0 || backdateInfo.exceedsLimit || rosterStatus === 'approved' || rosterStatus === 'submitted'}
                title={backdateInfo.exceedsLimit ? backdateInfo.message :
                       rosterStatus === 'approved' ? 'This roster is already approved' :
                       rosterStatus === 'submitted' ? 'This roster is pending approval' : ''}
                className="bg-[#891d33] hover:bg-[#6b1729]"
              >
                {rosterStatus === 'approved' ? 'Roster Approved' :
                 rosterStatus === 'submitted' ? 'Pending Approval' :
                 `Submit for Approval (${shifts.length} shifts)`}
              </Button>
            )}

            {/* Auto-save status indicator */}
            <div className="flex items-center gap-1 text-sm ml-auto">
              {loadingDraft && (
                <span className="text-gray-500 flex items-center gap-1">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading...
                </span>
              )}
              {!loadingDraft && saveStatus === 'saving' && (
                <span className="text-gray-500 flex items-center gap-1">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Saving...
                </span>
              )}
              {!loadingDraft && saveStatus === 'saved' && (
                <span className="text-green-600 flex items-center gap-1">
                  <Cloud className="h-4 w-4" />
                  Draft saved
                </span>
              )}
              {!loadingDraft && saveStatus === 'error' && (
                <span className="text-red-600 flex items-center gap-1">
                  <CloudOff className="h-4 w-4" />
                  Save failed
                </span>
              )}
              {!loadingDraft && saveStatus === 'idle' && draftRosterId && shifts.length > 0 && (
                <span className={`flex items-center gap-1 ${
                  rosterStatus === 'approved' ? 'text-green-600' :
                  rosterStatus === 'submitted' ? 'text-blue-600' : 'text-gray-400'
                }`}>
                  {rosterStatus === 'approved' ? (
                    <><ClipboardCheck className="h-4 w-4" /> Approved</>
                  ) : rosterStatus === 'submitted' ? (
                    <><Loader2 className="h-4 w-4" /> Pending</>
                  ) : (
                    <><Cloud className="h-4 w-4" /> Draft</>
                  )}
                </span>
              )}
            </div>
          </div>

          <div className="flex items-center gap-4 mb-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSelectedWeek(subDays(selectedWeek, 7))}
            >
              Previous Week
            </Button>
            <span className="text-sm text-gray-600 font-medium">
              Week: {format(weekStart, 'MMM d')} - {format(weekEnd, 'MMM d, yyyy')}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSelectedWeek(addDays(selectedWeek, 7))}
            >
              Next Week
            </Button>
          </div>

          <div className="grid grid-cols-7 gap-2">
            {Array.from({ length: 7 }, (_, i) => {
              const day = addDays(weekStart, i);
              const dayStr = format(day, 'yyyy-MM-dd');
              const dayShifts = shifts.filter(s => s.date === dayStr);

              return (
                <div key={i} className="border rounded p-2 min-h-[200px] bg-white">
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="font-semibold text-[#891d33]">{format(day, 'EEE')}</div>
                      <div className="text-sm text-gray-500">{format(day, 'MMM d')}</div>
                    </div>
                    {i > 0 && ( // Show copy button for all days except Monday
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-xs px-1 py-0 h-6 text-gray-500 hover:text-blue-600"
                        onClick={() => handleCopyPreviousDay(day)}
                        title={`Copy from ${format(subDays(day, 1), 'EEE')}`}
                      >
                        ← Copy
                      </Button>
                    )}
                  </div>

                  {dayShifts.map((shift, idx) => (
                    <div key={idx} className="text-xs mt-2 p-2 bg-blue-50 border border-blue-200 rounded relative group">
                      <div className="absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          className="text-blue-500 hover:text-blue-700 text-[10px] px-1"
                          onClick={() => handleEditShift(shift)}
                          title="Edit shift"
                        >
                          ✎
                        </button>
                        <button
                          className="text-red-400 hover:text-red-600"
                          onClick={() => handleDeleteShift(shift.shift_id)}
                          title="Remove shift"
                        >
                          ×
                        </button>
                      </div>
                      <div className="font-medium text-blue-800">{shift.user_name}</div>
                      <div className="text-blue-600">
                        {shift.locations[0].start_time} - {shift.locations[shift.locations.length - 1].end_time}
                      </div>
                      {shift.locations.map((loc, locIdx) => (
                        <div key={locIdx} className="text-gray-600 text-[10px]">
                          {loc.location_name}
                          {locIdx < shift.locations.length - 1 ? ' → ' : ''}
                        </div>
                      ))}
                    </div>
                  ))}

                  <Button
                    size="sm"
                    variant="ghost"
                    className="w-full mt-2 border-dashed border"
                    onClick={() => handleAddShift(day)}
                  >
                    + Add Shift
                  </Button>
                </div>
              );
            })}
          </div>

          {showShiftForm && (
            <ShiftForm
              date={selectedDate}
              editingShift={editingShift}
              onSave={handleSaveShift}
              onCancel={() => {
                setShowShiftForm(false);
                setEditingShift(null);
              }}
            />
          )}
        </div>
      </CardContent>
    </Card>
  );

  // Pending Approvals View Component (reusable)
  const PendingApprovalsView = () => (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ClipboardCheck className="h-5 w-5" />
          Pending Approvals
          {isManager && (
            <span className="ml-2 text-sm font-normal text-gray-500">
              (You can approve rosters for your assigned gyms)
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <Button onClick={fetchPendingRosters} variant="outline" disabled={loadingPending}>
            {loadingPending ? 'Loading...' : 'Refresh'}
          </Button>

          {/* Filter out the roster being edited so it only shows in one place */}
          {pendingRosters.filter(r => r.roster_id !== editingPendingRoster?.roster_id).length === 0 ? (
            <p className="text-gray-500">
              {editingPendingRoster ? 'Your roster is currently being edited in the Create Roster tab.' : 'No pending rosters to approve.'}
            </p>
          ) : (
            <div className="space-y-4">
              {pendingRosters.filter(r => r.roster_id !== editingPendingRoster?.roster_id).map((roster) => (
                <div key={roster.roster_id} className={`border rounded-lg p-4 ${!roster.can_approve && isManager ? 'bg-gray-50' : ''}`}>
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h3 className="font-semibold">
                        Week: {roster.week_start_date} to {roster.week_end_date}
                      </h3>
                      <p className="text-sm text-gray-600">
                        Created by: {roster.created_by_name} ({roster.created_by_role})
                      </p>
                      <p className="text-sm text-gray-600">
                        Team: {roster.team_name}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      {/* Edit button for own rosters */}
                      {roster.is_own_roster && isManager && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleEditPendingRoster(roster)}
                          className="border-blue-500 text-blue-600 hover:bg-blue-50"
                        >
                          ✎ Edit
                        </Button>
                      )}

                      {roster.can_approve ? (
                        <>
                          <Button
                            size="sm"
                            onClick={() => handleApproveRoster(roster.roster_id)}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => handleRejectRoster(roster.roster_id)}
                          >
                            Reject
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            disabled
                            title="You can only approve rosters for your assigned gyms"
                          >
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            disabled
                            title="You can only reject rosters for your assigned gyms"
                          >
                            Reject
                          </Button>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Own roster - needs admin approval */}
                  {roster.is_own_roster && isManager && (
                    <div className="mb-4 p-4 bg-amber-50 border-2 border-amber-400 rounded-lg">
                      <div className="flex items-start gap-3">
                        <div className="text-amber-500 text-2xl">⚠️</div>
                        <div>
                          <h4 className="font-bold text-amber-800 text-lg">Admin Approval Required</h4>
                          <p className="text-amber-700 mt-1">
                            This is your roster and requires approval from <strong>Admin</strong>.
                          </p>
                          <p className="text-amber-700 mt-2 text-sm">
                            Managers cannot approve their own rosters. Please contact the admin to review and approve your submitted roster.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Other gyms' rosters - view only */}
                  {!roster.can_approve && !roster.is_own_roster && isManager && (
                    <p className="text-xs text-gray-500 mb-2 italic">
                      View only - this roster is for gyms outside your assignment
                    </p>
                  )}

                  {/* Calendar-style view of shifts */}
                  <div className="text-sm">
                    <strong>Shifts ({roster.shifts?.length || 0}):</strong>
                    <div className="grid grid-cols-7 gap-2 mt-2">
                      {(() => {
                        // Parse week dates and create 7-day calendar
                        // Use date parts to avoid timezone issues
                        const [year, month, day] = roster.week_start_date.split('-').map(Number);
                        const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

                        return Array.from({ length: 7 }, (_, i) => {
                          // Use Date constructor to handle month overflow correctly
                          const displayDate = new Date(year, month - 1, day + i);
                          const dayStr = `${displayDate.getFullYear()}-${String(displayDate.getMonth() + 1).padStart(2, '0')}-${String(displayDate.getDate()).padStart(2, '0')}`;
                          const dayShifts = roster.shifts?.filter(s => s.date === dayStr) || [];

                          return (
                            <div key={i} className="border rounded p-2 min-h-[150px] bg-white">
                              <div className="font-semibold text-[#891d33]">{dayNames[i]}</div>
                              <div className="text-xs text-gray-500 mb-2">
                                {displayDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                              </div>

                              {dayShifts.length === 0 ? (
                                <div className="text-xs text-gray-400 italic">No shifts</div>
                              ) : (
                                dayShifts.map((shift, idx) => (
                                  <div key={idx} className="text-xs mt-1 p-2 bg-blue-50 border border-blue-200 rounded">
                                    <div className="font-medium text-blue-800">{shift.user_name}</div>
                                    <div className="text-blue-600">
                                      {shift.locations?.[0]?.start_time} - {shift.locations?.[shift.locations.length - 1]?.end_time}
                                    </div>
                                    {shift.locations?.map((loc, locIdx) => (
                                      <div key={locIdx} className="text-gray-600 text-[10px]">
                                        {loc.location_name}
                                      </div>
                                    ))}
                                  </div>
                                ))
                              )}
                            </div>
                          );
                        });
                      })()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );

  // Admin View: Pending Approvals and Active Rosters
  if (isAdmin) {
    return (
      <div className="p-6 space-y-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-4">
            <TabsTrigger value="pending" className="flex items-center gap-2">
              <ClipboardCheck className="h-4 w-4" />
              Pending Approvals
              {pendingRosters.length > 0 && (
                <span className="ml-1 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                  {pendingRosters.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="active" className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Active Rosters
              {approvedRosters.length > 0 && (
                <span className="ml-1 bg-green-600 text-white text-xs px-2 py-0.5 rounded-full">
                  {approvedRosters.length}
                </span>
              )}
            </TabsTrigger>
          </TabsList>
          <TabsContent value="pending">
            <PendingApprovalsView />
          </TabsContent>
          <TabsContent value="active">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  Active Rosters
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex gap-2 items-center">
                    <Button onClick={fetchApprovedRosters} variant="outline" disabled={loadingApproved}>
                      {loadingApproved ? 'Loading...' : 'Refresh'}
                    </Button>
                    <div className="flex gap-2 items-center">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSelectedWeek(subDays(selectedWeek, 7))}
                      >
                        Previous Week
                      </Button>
                      <span className="text-sm text-gray-600 font-medium">
                        {format(weekStart, 'MMM d')} - {format(weekEnd, 'MMM d, yyyy')}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSelectedWeek(addDays(selectedWeek, 7))}
                      >
                        Next Week
                      </Button>
                    </div>
                  </div>

                  {approvedRosters.length === 0 ? (
                    <p className="text-gray-500">No approved rosters for this week.</p>
                  ) : (
                    <div className="space-y-4">
                      {approvedRosters.map((roster) => (
                        <div key={roster.roster_id} className="border rounded-lg p-4 bg-green-50 border-green-200">
                          <div className="flex justify-between items-start mb-3">
                            <div>
                              <div className="flex items-center gap-2">
                                <h3 className="font-semibold text-green-800">
                                  {roster.team_name}
                                </h3>
                                <span className="bg-green-600 text-white text-xs px-2 py-1 rounded">
                                  APPROVED
                                </span>
                              </div>
                              <p className="text-sm text-gray-600 mt-1">
                                Week: {roster.week_start_date} to {roster.week_end_date}
                              </p>
                              <p className="text-sm text-gray-600">
                                Created by: {roster.created_by_name} ({roster.created_by_role})
                              </p>
                              {roster.admin_approved_by && (
                                <p className="text-sm text-gray-600">
                                  Approved: {new Date(roster.admin_approved_at).toLocaleString()}
                                </p>
                              )}
                            </div>
                          </div>

                          {/* Calendar-style view of shifts */}
                          <div className="text-sm">
                            <strong>Shifts ({roster.shifts?.length || 0}):</strong>
                            <div className="grid grid-cols-7 gap-2 mt-2">
                              {(() => {
                                if (!roster.week_start_date) return null;
                                const [year, month, day] = roster.week_start_date.split('-').map(Number);
                                const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

                                return Array.from({ length: 7 }, (_, i) => {
                                  // Use Date constructor to handle month overflow correctly
                                  const displayDate = new Date(year, month - 1, day + i);
                                  const dayStr = `${displayDate.getFullYear()}-${String(displayDate.getMonth() + 1).padStart(2, '0')}-${String(displayDate.getDate()).padStart(2, '0')}`;
                                  const dayShifts = roster.shifts?.filter(s => s.date === dayStr) || [];

                                  return (
                                    <div key={i} className="border rounded p-2 min-h-[150px] bg-white">
                                      <div className="font-semibold text-[#891d33]">{dayNames[i]}</div>
                                      <div className="text-xs text-gray-500 mb-2">
                                        {displayDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                      </div>

                                      {dayShifts.length === 0 ? (
                                        <div className="text-xs text-gray-400 italic">No shifts</div>
                                      ) : (
                                        dayShifts.map((shift, idx) => (
                                          <div key={idx} className="text-xs mt-1 p-2 bg-green-50 border border-green-200 rounded">
                                            <div className="font-medium text-green-800">{shift.user_name}</div>
                                            <div className="text-green-600">
                                              {shift.locations?.[0]?.start_time} - {shift.locations?.[shift.locations.length - 1]?.end_time}
                                            </div>
                                            {shift.locations?.map((loc, locIdx) => (
                                              <div key={locIdx} className="text-gray-600 text-[10px]">
                                                {loc.location_name}
                                              </div>
                                            ))}
                                          </div>
                                        ))
                                      )}
                                    </div>
                                  );
                                });
                              })()}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    );
  }

  // Manager View: Both Create Roster AND Approvals (FIXED!)
  if (isManager) {
    return (
      <div className="p-6 space-y-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-4">
            <TabsTrigger value="create" className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Create Roster
            </TabsTrigger>
            <TabsTrigger value="approvals" className="flex items-center gap-2">
              <ClipboardCheck className="h-4 w-4" />
              Pending Approvals
              {pendingRosters.length > 0 && (
                <span className="ml-1 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                  {pendingRosters.length}
                </span>
              )}
            </TabsTrigger>
          </TabsList>
          <TabsContent value="create">
            <RosterCreationView />
          </TabsContent>
          <TabsContent value="approvals">
            <PendingApprovalsView />
          </TabsContent>
        </Tabs>

        {/* Confirmation dialog for editing approved roster */}
        <AlertDialog open={showEditConfirmDialog} onOpenChange={setShowEditConfirmDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Edit Approved Roster?</AlertDialogTitle>
              <AlertDialogDescription>
                This roster has already been approved. Editing it will require admin re-approval
                before the changes take effect.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => {
                setShowEditConfirmDialog(false);
                setEditingRosterId(null);
                setEditingRosterStatus(null);
              }}>
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction onClick={() => {
                setShowEditConfirmDialog(false);
                toast.info('Editing roster - changes will require re-approval');
              }}>
                Continue Editing
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    );
  }

  // TL/FM View: No roster creation - redirect to My Roster
  // Roster creation is manager-only responsibility
  return (
    <div className="p-6 space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Roster Management
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <Calendar className="h-16 w-16 mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-semibold text-gray-700 mb-2">
              Rosters are Created by Your Manager
            </h3>
            <p className="text-gray-500 mb-6 max-w-md mx-auto">
              Your manager creates and submits the weekly roster for the team.
              You can view your assigned shifts in "My Roster".
            </p>
            <Button
              onClick={() => window.location.href = '/my-roster'}
              className="bg-[#891d33] hover:bg-[#6b1729]"
            >
              Go to My Roster
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
